<template>
  <div class="categories-manager">
    <!-- Header avec statistiques -->
    <div class="categories-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <i class="fas fa-layer-group"></i>
          </div>
          <div class="header-text">
            <h2>Gestion des Catégories</h2>
            <p>Gérez les catégories de produits de votre plateforme</p>
          </div>
        </div>
        <button @click="showCreateModal = true" class="btn-create">
          <i class="fas fa-plus"></i>
          <span>Nouvelle Catégorie</span>
        </button>
      </div>

      <!-- Statistiques -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fas fa-layer-group"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ categories.length }}</span>
            <span class="stat-label">Total Catégories</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon active">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ activeCategories }}</span>
            <span class="stat-label">Actives</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon inactive">
            <i class="fas fa-times-circle"></i>
          </div>
          <div class="stat-content">
            <span class="stat-value">{{ inactiveCategories }}</span>
            <span class="stat-label">Inactives</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Tableau des catégories -->
    <div class="categories-table-container">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Chargement des catégories...</p>
      </div>

      <div v-else-if="categories.length === 0" class="empty-state">
        <i class="fas fa-layer-group"></i>
        <h3>Aucune catégorie</h3>
        <p>Créez votre première catégorie pour commencer</p>
        <button @click="showCreateModal = true" class="btn-create-empty">
          <i class="fas fa-plus"></i>
          Créer une catégorie
        </button>
      </div>

      <!-- Vue Desktop: Tableau -->
      <table v-else class="categories-table desktop-view">
        <thead>
          <tr>
            <th>Emoji</th>
            <th>Nom</th>
            <th>Description</th>
            <th>Couleur</th>
            <th>Ordre</th>
            <th>Statut</th>
            <th>
              <div class="prospects-header">
                <i class="fas fa-users"></i>
                <span>Afficher dans Prospects</span>
              </div>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="category in sortedCategories" :key="category._id">
            <td>
              <div class="category-emoji">{{ category.emoji }}</div>
            </td>
            <td>
              <div class="category-name">{{ category.nom }}</div>
            </td>
            <td>
              <div class="category-description">
                {{ category.description || '-' }}
              </div>
            </td>
            <td>
              <div class="category-color">
                <div class="color-preview" :style="{ backgroundColor: category.couleur }"></div>
                <span>{{ category.couleur }}</span>
              </div>
            </td>
            <td>
              <div class="category-ordre">{{ category.ordre }}</div>
            </td>
            <td>
              <span class="status-badge" :class="category.actif ? 'active' : 'inactive'">
                <i :class="category.actif ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                {{ category.actif ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>
              <div class="checkbox-container">
                <label class="checkbox-wrapper">
                  <input 
                    type="checkbox"
                    :checked="category.afficherDansProspects === true || category.afficherDansProspects === undefined"
                    @change="toggleProspectsDisplay(category)"
                    class="checkbox-input"
                  />
                  <span class="checkbox-custom"></span>
                  <span class="checkbox-label">{{ (category.afficherDansProspects === true || category.afficherDansProspects === undefined) ? 'Oui' : 'Non' }}</span>
                </label>
              </div>
            </td>
            <td>
              <div class="action-buttons">
                <button @click="editCategory(category)" class="btn-action edit" title="Modifier">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="deleteCategory(category)" class="btn-action delete" title="Supprimer">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Vue Mobile: Cartes -->
      <div v-if="categories.length > 0" class="mobile-view">
        <div class="categories-cards">
          <div 
            v-for="category in sortedCategories" 
            :key="category._id"
            class="category-card"
          >
            <div class="card-header" :style="{ borderLeftColor: category.couleur }">
              <div class="category-emoji-large">{{ category.emoji }}</div>
              <div class="category-main-info">
                <div class="category-name">{{ category.nom }}</div>
                <div class="category-description-mobile">{{ category.description || 'Aucune description' }}</div>
              </div>
              <span class="status-indicator" :class="category.actif ? 'active' : 'inactive'"></span>
            </div>
            
            <div class="card-body">
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-palette"></i>
                  Couleur
                </span>
                <div class="category-color">
                  <div class="color-preview" :style="{ backgroundColor: category.couleur }"></div>
                  <span>{{ category.couleur }}</span>
                </div>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-sort-numeric-up"></i>
                  Ordre
                </span>
                <span class="category-ordre">{{ category.ordre }}</span>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-toggle-on"></i>
                  Statut
                </span>
                <span class="status-badge" :class="category.actif ? 'active' : 'inactive'">
                  <i :class="category.actif ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                  {{ category.actif ? 'Active' : 'Inactive' }}
                </span>
              </div>
              
              <div class="info-row">
                <span class="info-label">
                  <i class="fas fa-users"></i>
                  Prospects
                </span>
                <label class="checkbox-wrapper-mobile">
                  <input 
                    type="checkbox"
                    :checked="category.afficherDansProspects === true || category.afficherDansProspects === undefined"
                    @change="toggleProspectsDisplay(category)"
                    class="checkbox-input"
                  />
                  <span class="checkbox-custom"></span>
                  <span class="checkbox-label">{{ (category.afficherDansProspects === true || category.afficherDansProspects === undefined) ? 'Oui' : 'Non' }}</span>
                </label>
              </div>
            </div>
            
            <div class="card-actions">
              <button @click="editCategory(category)" class="btn-card-action edit">
                <i class="fas fa-edit"></i>
                Modifier
              </button>
              <button @click="deleteCategory(category)" class="btn-card-action delete">
                <i class="fas fa-trash"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Créer/Modifier Catégorie -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-header-content">
            <div class="modal-icon">
              <i class="fas fa-layer-group"></i>
            </div>
            <div class="modal-title-section">
              <h3>{{ isEditing ? 'Modifier la Catégorie' : 'Nouvelle Catégorie' }}</h3>
              <p>{{ isEditing ? 'Modifiez les informations de la catégorie' : 'Créez une nouvelle catégorie de produits' }}</p>
            </div>
          </div>
          <button @click="closeModal" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <div class="form-grid">
              <!-- Nom -->
              <div class="form-group full-width">
                <label>Nom de la catégorie *</label>
                <input 
                  v-model="form.nom" 
                  type="text" 
                  placeholder="Ex: GPS Tracker"
                  required
                  class="form-input"
                />
              </div>

              <!-- Emoji -->
              <div class="form-group">
                <label>Emoji</label>
                <input 
                  v-model="form.emoji" 
                  type="text" 
                  placeholder="📦"
                  maxlength="2"
                  class="form-input emoji-input"
                />
              </div>

              <!-- Couleur -->
              <div class="form-group">
                <label>Couleur</label>
                <div class="color-input-wrapper">
                  <input 
                    v-model="form.couleur" 
                    type="color" 
                    class="form-input-color"
                  />
                  <input 
                    v-model="form.couleur" 
                    type="text" 
                    placeholder="#001BB7"
                    class="form-input color-text"
                  />
                </div>
              </div>

              <!-- Ordre -->
              <div class="form-group">
                <label>Ordre d'affichage</label>
                <input 
                  v-model.number="form.ordre" 
                  type="number" 
                  min="0"
                  placeholder="0"
                  class="form-input"
                />
              </div>

              <!-- Statut -->
              <div class="form-group">
                <label>Statut</label>
                <div class="toggle-wrapper">
                  <input 
                    v-model="form.actif" 
                    type="checkbox" 
                    id="actif-toggle"
                    class="toggle-input"
                  />
                  <label for="actif-toggle" class="toggle-label">
                    <span class="toggle-button"></span>
                    <span class="toggle-text">{{ form.actif ? 'Active' : 'Inactive' }}</span>
                  </label>
                </div>
              </div>

              <!-- Afficher dans Prospects -->
              <div class="form-group">
                <label>
                  <i class="fas fa-users"></i>
                  Afficher dans Prospects
                </label>
                <div class="toggle-wrapper">
                  <input 
                    v-model="form.afficherDansProspects" 
                    type="checkbox" 
                    id="prospects-toggle"
                    class="toggle-input"
                  />
                  <label for="prospects-toggle" class="toggle-label">
                    <span class="toggle-button"></span>
                    <span class="toggle-text">{{ form.afficherDansProspects ? 'Oui' : 'Non' }}</span>
                  </label>
                </div>
              </div>

              <!-- Description -->
              <div class="form-group full-width">
                <label>Description</label>
                <textarea 
                  v-model="form.description" 
                  placeholder="Description de la catégorie (optionnel)"
                  rows="3"
                  class="form-textarea"
                ></textarea>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" @click="closeModal" class="btn-cancel">
                <i class="fas fa-times"></i>
                Annuler
              </button>
              <button type="submit" class="btn-submit" :disabled="submitting">
                <i :class="submitting ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                {{ submitting ? 'En cours...' : (isEditing ? 'Modifier' : 'Créer') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api';
import { useToast } from 'vue-toastification';

const toast = useToast();

// État
const categories = ref([]);
const loading = ref(false);
const submitting = ref(false);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedCategory = ref(null);

// Formulaire
const form = ref({
  nom: '',
  emoji: '📦',
  description: '',
  couleur: '#001BB7',
  actif: true,
  afficherDansProspects: true,
  ordre: 0
});

// Computed
const isEditing = computed(() => showEditModal.value && selectedCategory.value);

const sortedCategories = computed(() => {
  return [...categories.value].sort((a, b) => a.ordre - b.ordre);
});

const activeCategories = computed(() => {
  return categories.value.filter(c => c.actif).length;
});

const inactiveCategories = computed(() => {
  return categories.value.filter(c => !c.actif).length;
});

// Méthodes
const loadCategories = async () => {
  loading.value = true;
  try {
    const response = await api.get('/categories');
    categories.value = response.data.data;
    
    // Debug: Afficher l'état de afficherDansProspects pour chaque catégorie
    console.log('📋 Catégories chargées:');
    categories.value.forEach(cat => {
      console.log(`  - ${cat.nom}: afficherDansProspects = ${cat.afficherDansProspects}`);
    });
  } catch (error) {
    console.error('Erreur chargement catégories:', error);
    toast.error('Erreur lors du chargement des catégories');
  } finally {
    loading.value = false;
  }
};

const editCategory = (category) => {
  selectedCategory.value = category;
  form.value = {
    nom: category.nom,
    emoji: category.emoji,
    description: category.description || '',
    couleur: category.couleur,
    actif: category.actif,
    afficherDansProspects: category.afficherDansProspects === true || category.afficherDansProspects === undefined,
    ordre: category.ordre
  };
  showEditModal.value = true;
};

const deleteCategory = async (category) => {
  if (!confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${category.nom}" ?`)) {
    return;
  }

  try {
    await api.delete(`/categories/${category._id}`);
    toast.success('Catégorie supprimée avec succès');
    await loadCategories();
  } catch (error) {
    console.error('Erreur suppression catégorie:', error);
    const message = error.response?.data?.message || 'Erreur lors de la suppression';
    toast.error(message);
  }
};

const submitForm = async () => {
  submitting.value = true;
  try {
    const url = isEditing.value 
      ? `/categories/${selectedCategory.value._id}`
      : '/categories';
    
    const method = isEditing.value ? 'put' : 'post';
    
    await api[method](url, form.value);

    toast.success(isEditing.value ? 'Catégorie modifiée avec succès' : 'Catégorie créée avec succès');
    closeModal();
    await loadCategories();
  } catch (error) {
    console.error('Erreur soumission formulaire:', error);
    const message = error.response?.data?.message || 'Erreur lors de l\'opération';
    toast.error(message);
  } finally {
    submitting.value = false;
  }
};

const toggleProspectsDisplay = async (category) => {
  try {
    // Calculer la nouvelle valeur : si true ou undefined, passer à false, sinon passer à true
    const currentValue = category.afficherDansProspects === true || category.afficherDansProspects === undefined;
    const newValue = !currentValue;
    
    console.log(`🔄 Toggle ${category.nom}: ${currentValue} → ${newValue}`);
    
    const response = await api.put(
      `/categories/${category._id}`,
      { afficherDansProspects: newValue }
    );
    
    console.log(`✅ Réponse API pour ${category.nom}:`, response.data.data.afficherDansProspects);
    
    // Mettre à jour localement
    category.afficherDansProspects = newValue;
    
    toast.success(
      newValue 
        ? `La catégorie "${category.nom}" sera affichée dans les prospects`
        : `La catégorie "${category.nom}" sera masquée dans les prospects`
    );
  } catch (error) {
    console.error('Erreur toggle prospects display:', error);
    toast.error('Erreur lors de la mise à jour');
    // Recharger pour annuler le changement visuel
    await loadCategories();
  }
};

const closeModal = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  selectedCategory.value = null;
  form.value = {
    nom: '',
    emoji: '📦',
    description: '',
    couleur: '#001BB7',
    actif: true,
    afficherDansProspects: true,
    ordre: 0
  };
};

// Lifecycle
onMounted(() => {
  loadCategories();
});
</script>

<style scoped>
.categories-manager {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.categories-header {
  background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85));
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0,27,183,0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.75rem;
  box-shadow: 0 4px 15px rgba(0,27,183,0.3);
}

.header-text h2 {
  font-size: 1.75rem;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.25rem;
}

.header-text p {
  color: #6c757d;
  font-size: 0.95rem;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,27,183,0.3);
}

.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,27,183,0.4);
}

/* Statistiques */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(226,232,240,0.8);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  animation: fadeInUp 0.6s ease backwards;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0,27,183,0.15);
  border-color: rgba(0,27,183,0.3);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.stat-icon.total {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
}

.stat-icon.active {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.inactive {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #000000;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

/* Tableau */
.categories-table-container {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,27,183,0.08);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-state i,
.empty-state i {
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #64748b;
  margin-bottom: 2rem;
}

.btn-create-empty {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.categories-table {
  width: 100%;
  border-collapse: collapse;
}

.categories-table thead {
  background: linear-gradient(135deg, rgba(0,27,183,0.05), rgba(0,70,255,0.05));
}

.categories-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.categories-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  animation: fadeInRow 0.5s ease backwards;
}

.categories-table tbody tr:nth-child(1) { animation-delay: 0.05s; }
.categories-table tbody tr:nth-child(2) { animation-delay: 0.1s; }
.categories-table tbody tr:nth-child(3) { animation-delay: 0.15s; }
.categories-table tbody tr:nth-child(4) { animation-delay: 0.2s; }
.categories-table tbody tr:nth-child(5) { animation-delay: 0.25s; }
.categories-table tbody tr:nth-child(6) { animation-delay: 0.3s; }
.categories-table tbody tr:nth-child(7) { animation-delay: 0.35s; }
.categories-table tbody tr:nth-child(8) { animation-delay: 0.4s; }
.categories-table tbody tr:nth-child(9) { animation-delay: 0.45s; }
.categories-table tbody tr:nth-child(10) { animation-delay: 0.5s; }

@keyframes fadeInRow {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.categories-table tbody tr:hover {
  background: rgba(0,27,183,0.02);
  transform: scale(1.01);
  box-shadow: 0 2px 10px rgba(0,27,183,0.08);
}

.categories-table td {
  padding: 1rem;
}

.category-emoji {
  font-size: 2rem;
}

.category-name {
  font-weight: 600;
  color: #1e293b;
}

.category-description {
  color: #64748b;
  font-size: 0.875rem;
}

.category-color {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.color-preview {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
}

.category-ordre {
  font-weight: 600;
  color: #64748b;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
}

.status-badge.active {
  background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.1));
  color: #059669;
  border: 1px solid rgba(16,185,129,0.3);
}

.status-badge.inactive {
  background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.1));
  color: #dc2626;
  border: 1px solid rgba(239,68,68,0.3);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Colonne Prospects */
.prospects-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
}

.prospects-header i {
  font-size: 1rem;
}

.checkbox-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  display: none;
}

.checkbox-custom {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(0, 27, 183, 0.3);
  border-radius: 6px;
  display: inline-block;
  position: relative;
  transition: all 0.3s ease;
  background: white;
}

.checkbox-input:checked + .checkbox-custom {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-color: var(--primary-blue);
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '\f00c';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.7rem;
}

.checkbox-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
}

.checkbox-input:checked ~ .checkbox-label {
  color: var(--primary-blue);
}

.btn-action {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
}

.btn-action::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.4s ease, height 0.4s ease;
}

.btn-action:active::before {
  width: 100px;
  height: 100px;
}

.btn-action.edit {
  background: linear-gradient(135deg, rgba(0,27,183,0.1), rgba(0,70,255,0.1));
  color: var(--primary-blue);
}

.btn-action.edit:hover {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 12px rgba(0,27,183,0.3);
}

.btn-action.delete {
  background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(220,38,38,0.1));
  color: #dc2626;
}

.btn-action.delete:hover {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 12px rgba(239,68,68,0.3);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 20px 20px 0 0;
  color: white;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.modal-icon {
  width: 50px;
  height: 50px;
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.modal-title-section h3 {
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 0.25rem;
}

.modal-title-section p {
  font-size: 0.875rem;
  opacity: 0.9;
}

.btn-close {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-close:hover {
  background: rgba(255,255,255,0.3);
  transform: scale(1.05);
}

.modal-body {
  padding: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-group label i {
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.form-input,
.form-textarea {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.9375rem;
  transition: all 0.3s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0,27,183,0.1);
}

.emoji-input {
  font-size: 1.5rem;
  text-align: center;
}

.color-input-wrapper {
  display: flex;
  gap: 0.5rem;
}

.form-input-color {
  width: 60px;
  height: 44px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
}

.color-text {
  flex: 1;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
}

.toggle-input {
  display: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.toggle-button {
  position: relative;
  width: 50px;
  height: 26px;
  background: #cbd5e1;
  border-radius: 13px;
  transition: all 0.3s ease;
}

.toggle-button::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.toggle-input:checked + .toggle-label .toggle-button {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
}

.toggle-input:checked + .toggle-label .toggle-button::after {
  transform: translateX(24px);
}

.toggle-text {
  font-weight: 600;
  color: #64748b;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.btn-cancel,
.btn-submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-cancel {
  background: #f1f5f9;
  color: #64748b;
}

.btn-cancel:hover {
  background: #e2e8f0;
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 15px rgba(0,27,183,0.3);
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,27,183,0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Vue Desktop/Mobile toggle */
.desktop-view {
  display: table;
  width: 100%;
}

.mobile-view {
  display: none;
}

/* Styles pour les cartes mobiles */
.categories-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 4px solid var(--primary-blue);
}

.category-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.category-card .card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.03) 0%, rgba(0, 70, 255, 0.03) 100%);
  border-bottom: 1px solid #e2e8f0;
  position: relative;
}

.category-emoji-large {
  font-size: 2.5rem;
  flex-shrink: 0;
}

.category-main-info {
  flex: 1;
  min-width: 0;
}

.category-main-info .category-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.category-description-mobile {
  font-size: 0.85rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-indicator.active {
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.status-indicator.inactive {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

.category-card .card-body {
  padding: 1rem 1.25rem;
}

.category-card .info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.category-card .info-row:last-child {
  border-bottom: none;
}

.category-card .info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.category-card .info-label i {
  width: 16px;
  text-align: center;
  color: var(--primary-blue);
}

.checkbox-wrapper-mobile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.category-card .card-actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.btn-card-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-card-action.edit {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.1), rgba(0, 70, 255, 0.1));
  color: var(--primary-blue);
}

.btn-card-action.edit:hover {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-card-action.delete {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1));
  color: #dc2626;
}

.btn-card-action.delete:hover {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

/* Responsive */
@media (max-width: 1024px) {
  .categories-manager {
    padding: 1.5rem;
  }

  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .categories-manager {
    padding: 1rem;
  }

  .categories-header {
    padding: 1.5rem;
    border-radius: 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .header-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .header-text h2 {
    font-size: 1.5rem;
  }

  .btn-create {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .stat-icon {
    width: 45px;
    height: 45px;
    font-size: 1.25rem;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  /* Masquer le tableau, afficher les cartes */
  .desktop-view {
    display: none !important;
  }

  .mobile-view {
    display: block;
  }

  .categories-table-container {
    padding: 1rem;
    border-radius: 16px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-container {
    margin: 0.5rem;
    max-height: 95vh;
  }

  .modal-header {
    padding: 1.5rem;
  }

  .modal-header-content {
    gap: 1rem;
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .modal-title-section h3 {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .btn-cancel,
  .btn-submit {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .categories-manager {
    padding: 0.75rem;
  }

  .categories-header {
    padding: 1rem;
  }

  .header-text h2 {
    font-size: 1.25rem;
  }

  .header-text p {
    font-size: 0.85rem;
  }

  .category-card .card-actions {
    flex-direction: column;
  }

  .btn-card-action {
    width: 100%;
  }
}
</style>
